import { inject, Injectable } from '@angular/core';
import { Popover } from 'primeng/popover';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { IProject } from '@app/api/interfaces/project.interface';
import { IArea } from '@app/api/interfaces/area.interface';
import { BehaviorSubject, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import { ITimeSelectionItem } from '@app/main/main-screen/interfaces/time-selection';
import { Store } from '@ngrx/store';
import {
  selectMainDate,
  selectMainProjectId,
} from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectAllProjects, selectProjectById } from '@app/main/main-screen/store/projects/projects.selectors';
import { selectAreasById, selectAreasByProjectId } from '@app/main/main-screen/store/areas/areas.selectors';
import { Table } from 'primeng/table';
import {
  selectMainAreaId,
  selectMainProjectSelectionIsLoading,
} from '@app/main/main-screen/store/main-selection/main-project-selection/main-project-selection.selectors';
import { shareReplay } from 'rxjs/operators';
import { ITimeSelection } from '@app/main/main-screen/interfaces/time-selection';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import equal from 'fast-deep-equal';

@Injectable({
  providedIn: 'root',
})
export class DataSelectionService {
  private store: Store = inject(Store);
  private scrRes: ScreenResolutionService = inject(ScreenResolutionService);

  public dateFormat = DayJSDateFormat.MMMDDYYYY;

  public loading = this.store.select(selectMainProjectSelectionIsLoading);

  public projects: Observable<IProject[]> = this.store
    .select(selectAllProjects)
    .pipe(map(projects => projects.slice()));

  public selectedProject: Observable<IProject | undefined> = this.store
    .select(selectMainProjectId)
    .pipe(switchMap(projectId => (projectId ? this.store.select(selectProjectById(projectId)) : of(undefined))));

  public areas: Observable<IArea[]> = this.store
    .select(selectMainProjectId)
    .pipe(switchMap(projectId => (projectId ? this.store.select(selectAreasByProjectId(projectId)) : of([]))));

  public selectedArea: Observable<IArea | undefined> = this.store
    .select(selectMainAreaId)
    .pipe(switchMap(areaId => (areaId ? this.store.select(selectAreasById(areaId)) : of(undefined))));

  public selectedDate: Observable<string | undefined> = this.store.select(selectMainDate);

  private _timeSelectionItems = new BehaviorSubject<ITimeSelectionItem[]>([]);
  public timeSelectionItems: Observable<ITimeSelectionItem[]> = this._timeSelectionItems.asObservable();
  public setTimeSelectionItems(items: ITimeSelectionItem[]): void {
    this._timeSelectionItems.next(items);
  }

  private _timeSelection = new BehaviorSubject<ITimeSelection | null>(null);
  public timeSelection: Observable<ITimeSelection | null> = this._timeSelection
    .asObservable()
    .pipe(distinctUntilChanged(equal), shareReplay({ bufferSize: 1, refCount: true }));
  public setTimeSelection(timeSelection: ITimeSelection | null): void {
    this._timeSelection.next(timeSelection);
  }

  public getPopoverContentHeight(popover?: Popover): string {
    if (popover?.overlayVisible || !popover) {
      return 'auto';
    }

    const headerHeight = document.querySelector('gc-header')?.clientHeight || 0;
    const subHeaderHeight = document.querySelector('#sub-header')?.clientHeight || 0;
    const popoverPadding = 12; // popover content padding;
    const defaultMaxPopoverContentHeight = 692;

    const extraSpace = headerHeight + subHeaderHeight + popoverPadding * 2;

    if (this.scrRes.isScreenMdUp() && this.scrRes.screenHeight() > defaultMaxPopoverContentHeight + extraSpace) {
      return defaultMaxPopoverContentHeight + 'px';
    } else {
      let fix: number; // to fix jumping

      if (this.scrRes.isScreenMdUp()) {
        fix = 18;
      } else if (this.scrRes.isScreenSmUp()) {
        fix = 5;
      } else {
        fix = 1;
      }

      if (CSS.supports('height', '100dvh')) {
        return `calc(100dvh - ${extraSpace + fix}px)`;
      } else {
        return `calc(100vh - ${extraSpace + fix}px)`;
      }
    }
  }

  public scrollToSelectedRow(table: Table): void {
    if (!table || !table.el) return;

    const tableEl: HTMLElement = table.el.nativeElement;
    const selectedRow = tableEl.querySelector<HTMLElement>('tr.p-datatable-row-selected');

    if (selectedRow) {
      selectedRow.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }
}
